<script setup lang="ts">
import { computed } from 'vue';

import { metaRaw } from '@/utils/dataTable';
import { type FileKind, getFileKindFromMime } from '@/utils/files';

import type { DataTableCellProps } from './templateTypes';

const props = defineProps<DataTableCellProps>();

const rawMime = computed(() => metaRaw<string>(props.cell));

/** PrimeIcons mapping (https://primefaces.org/primevue/icons) */
function getPrimeIconForKind(kind: FileKind): string {
  switch (kind) {
    case 'pdf':
      return 'pi-file-pdf';
    case 'image':
      return 'pi-image';
    case 'excel':
      return 'pi-file-excel';
    case 'csv':
      return 'pi-file-excel';
    case 'word':
      return 'pi-file-word';
    case 'powerpoint':
      return 'pi-file';
    case 'text':
      return 'pi-file';
    case 'audio':
      return 'pi-volume-up';
    case 'video':
      return 'pi-video';
    case 'zip':
      return 'pi-folder';
    default:
      return 'pi-file';
  }
}

function getColorClassForKind(kind: FileKind): string {
  switch (kind) {
    case 'pdf':
      return 'text-red-600';
    case 'image':
      return 'text-amber-500';
    case 'excel':
      return 'text-emerald-600';
    case 'csv':
      return 'text-gray-600';
    case 'word':
      return 'text-blue-600';
    case 'powerpoint':
      return 'text-orange-600';
    case 'text':
      return 'text-slate-600';
    case 'audio':
      return 'text-indigo-600';
    case 'video':
      return 'text-fuchsia-600';
    case 'zip':
      return 'text-amber-700';
    default:
      return 'text-slate-600';
  }
}

// Derive kind + icon
const fileKind = computed(() => getFileKindFromMime(rawMime.value));
const primeIconClass = computed(() => `pi ${getPrimeIconForKind(fileKind.value)}`);
const colorClass = computed(() => getColorClassForKind(fileKind.value));
</script>

<template>
  <ExpandableCell :data-key="dataKey" :field="column.field">
    <!-- Collapsed content -->
    <template #collapsed>
      <div class="text-center">
        <i
          v-tooltip="{
            value: rawMime ?? '',
            fitContent: true,
            showDelay: 300,
          }"
          :class="[primeIconClass, colorClass]"
          aria-hidden="true"
        />
      </div>
    </template>

    <!-- Expanded content -->
    <template #expanded>
      <div>
        {{ cell.value }}
      </div>
    </template>
  </ExpandableCell>
</template>
